/**
 * The two explicit codecs between the domain `Money` and the `MoneyWire` a DTO
 * or realtime payload carries (ADR 060 — Money Type § 2.2, § 2.7).
 */
import { isSupportedCurrency } from './currency';
import { Money } from './money';

/**
 * A monetary amount on the wire — an exact decimal string (as the `numeric`
 * driver returns it, never a float) that always travels with its currency
 * (ADR 060 — Money Type § 2.3). A DTO declares `MoneyWire`, never a bare
 * `number` / `string` amount.
 *
 * Encode with {@link toWire}, decode with {@link fromWire}. Still unbranded:
 * existing producers build it as a literal, and branding it (so a raw
 * `{ amount, currency }` cannot pass for one) is the follow-up once they all
 * go through `toWire` (BL-1823).
 */
export interface MoneyWire {
    /** Exact decimal string, e.g. `"60.50"`. */
    amount: string;
    /** ISO 4217 code the amount is denominated in. */
    currency: string;
}

/** Wire amounts carry the full storage scale, matching the numeric(18,4) driver string. */
const WIRE_SCALE = 4;

/**
 * Encodes a `Money` for the wire. The amount is written at four decimals, so it
 * is byte-identical to what the `numeric(18,4)` driver returned before the
 * custom type existed (`Decimal#toString` would give `"300"`, not `"300.0000"`).
 *
 * @param money - The domain value.
 * @returns `{ amount, currency }` — always both (ADR 060 § 2.3).
 */
export function toWire(money: Money): MoneyWire {
    return { amount: money.toDecimal().toFixed(WIRE_SCALE), currency: money.currency };
}

/**
 * Decodes a wire value back into a `Money`.
 *
 * @param wire - The `{ amount, currency }` pair off a DTO.
 * @returns The domain value.
 * @throws TypeError when the currency is not supported or the amount is not a finite decimal.
 */
export function fromWire(wire: MoneyWire): Money {
    if (!isSupportedCurrency(wire.currency)) {
        throw new TypeError(`MoneyWire currency "${wire.currency}" is not a supported currency`);
    }
    return Money.of(wire.amount, wire.currency);
}
